import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { HiddenProps } from "@insite/mobius/Hidden";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    getRmaAttachedFiles,
    getRmaLinesRaw,
    getRmaNotes,
    getRmaProductInfos,
    getRmaReasons,
} from "@store/Selectors/Rma/RmaSelectors";
import rmaRequest from "@store/Store/Components/OrderHistoryProductSelector/Handlers/RmaRequest";
import clearRmaNotes from "@store/Store/Data/RmaRequest/Handlers/ClearRmaNotes";
import clearRmaRequest from "@store/Store/Data/RmaRequest/Handlers/ClearRmaRequest";
import clearProducts from "@store/Store/Pages/Rma/Handlers/ClearProducts";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { RmaLineModel } from "@store/Types/ApiModels";
import React, { useCallback, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: StoreApplicationState) => {
    return {
        productInfos: getRmaProductInfos(state),
        rmaReasons: getRmaReasons(state),
        rmaLinesRaw: getRmaLinesRaw(state),
        rmaNotes: getRmaNotes(state),
        rmaAttachedFiles: getRmaAttachedFiles(state),
        state,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addCartLines: makeHandlerChainAwaitable(addCartLines),
    clearProducts,
    clearRmaRequest,
    clearRmaNotes,
    loadCurrentCart,
    rmaRequest,
};

type Props = ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    HasToasterContext;

export interface RmaOrderHistoryActionsStyles {
    wrapper?: InjectableCss;
    buttonsHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    addAllToCartButton?: ButtonPresentationProps;
}

export const actionsStyles: RmaOrderHistoryActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-flow: column;
                    `,
                    css`
                        flex-flow: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    addAllToCartButton: {
        variant: "primary",
        css: css`
            margin-left: 10px;
        `,
    },
};

const styles = actionsStyles;

const RmaOrderHistoryActions = ({
    rmaLinesRaw,
    rmaNotes,
    rmaRequest,
    rmaReasons,
    clearProducts,
    clearRmaRequest,
    clearRmaNotes,
    productInfos,
    toaster,
    rmaAttachedFiles,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const productsExist = rmaLinesRaw && rmaLinesRaw.length > 0;

    const isRmaLinesValid = useCallback(
        () =>
            rmaLinesRaw.length > 0 &&
            rmaLinesRaw.every(o => {
                const needsSerialNumber =
                    productInfos.find(pi => pi.lineId === o.LineId)?.autocompleteItemModel.rmaNeedsSn ?? false;
                return (
                    (o.ReturnReason?.length ?? 0) > 0 &&
                    o.ReturnedQuantity > 0 &&
                    (needsSerialNumber ? (o.SerialNumber?.length ?? 0) > 0 : true)
                );
            }),
        [rmaLinesRaw],
    );

    if (!productsExist) {
        return null;
    }

    const systemReasonString = (key: string | undefined) => (key ? rmaReasons[key] : rmaReasons);

    const reasonsToString = (rmaLineModels: RmaLineModel[]) => {
        const newRmaLines: RmaLineModel[] = [];
        // eslint-disable-next-line array-callback-return
        rmaLineModels.map((line: RmaLineModel) => {
            newRmaLines.push({
                LineId: line.LineId,
                ProductErpNumber: line.ProductErpNumber,
                ProductDescription: line.ProductDescription,
                ReturnReason: systemReasonString(line.ReturnReason),
                ReturnedQuantity: line.ReturnedQuantity,
                InvoiceNumber: line.InvoiceNumber,
                SerialNumber: line.SerialNumber,
                LineNotes: "",
            });
        });
        return newRmaLines;
    };

    const requestClickHandler = () => {
        if (!isRmaLinesValid()) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("Product quantities and return reasons are not valid.")}</Typography>
                    </>
                ),
                messageType: "danger",
                timeoutLength: 4000,
            });
            return;
        }

        const rmaLineModels: RmaLineModel[] = reasonsToString(rmaLinesRaw);
        rmaRequest({
            rmaNotes,
            rmaLineModels,
            rmaAttachedFiles,
            onSuccess: () => {
                toasterContext.addToast({ body: siteMessage("RmaRequest_Success"), messageType: "success" });
                clearRmaRequest();
                clearProducts();
                clearRmaNotes();
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({
                    body: errorMessage,
                    messageType: "danger",
                });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {productsExist && (
                <Button {...styles.addAllToCartButton} onClick={requestClickHandler}>
                    {translate("Request return")}
                </Button>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(RmaOrderHistoryActions))),
    definition: {
        displayName: "RMA Submit Action Button",
        group: "RMA" as any,
    },
};

export default widgetModule;
